import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import ModalV2 from '/@/components/bayer/modal-v2';
import Spinner from '/@/components/spinner';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import type { Field } from '/@/types/field';
import PhenologyTimeline, { PhenologyLegend } from './phenology-timeline';
import { usePhenology } from './use-phenology';

/**
 * "Fenología" tab: stage dates for the chosen planting date, plus a sandbox for trying another
 * one. The simulation is read-only until "Aplicar fecha" — applying it changes the crop config,
 * which invalidates the environment, so the wizard sends the user back to reconfigure it.
 */
function PhenologyTab({
    field,
    crop = 'corn',
    plantingDate,
    compact,
    onPlantingDateApplied,
}: {
    field: Field;
    crop?: 'corn' | 'soy';
    plantingDate?: string;
    compact?: boolean;
    /** Called once the new planting date is stored, to send the user back to the environment step. */
    onPlantingDateApplied: () => void;
}) {
    const dispatch = useDispatch();
    const { hybrids, isLoading } = usePhenology({ field, crop, plantingDate });

    const [draftDate, setDraftDate] = useState<string | undefined>(plantingDate);
    const [simulatedDate, setSimulatedDate] = useState<string | undefined>(undefined);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const simulation = usePhenology({ field, crop, plantingDate: simulatedDate });

    const canSimulate = useMemo(
        () => !!draftDate && draftDate !== plantingDate && dayjs(draftDate).isValid(),
        [draftDate, plantingDate]
    );

    const applyDate = () => {
        if (!simulatedDate) return;
        dispatch(updateActiveRecommendationCropConfig({ key: 'plantingDate', value: simulatedDate }));
        // The hybrids were chosen for the old environment, so they no longer apply.
        dispatch(updateActiveRecommendationCropConfig({ key: 'products', value: [] }));
        setConfirmOpen(false);
        onPlantingDateApplied();
    };

    if (isLoading && !hybrids.length) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    return (
        <Stack spacing={3}>
            <Stack>
                <Typography variant='h6' fontWeight='bold' color='primary'>
                    {_('Fenología según la fecha de siembra')}
                </Typography>
                <Typography variant='body2' color='primary'>
                    {_('Fechas de cada estadio y período crítico según la fecha de siembra.')}
                </Typography>
            </Stack>

            <PhenologyTimeline hybrids={hybrids} compact={compact} />
            <PhenologyLegend />

            <Stack
                spacing={2}
                p={2}
                sx={{
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.divider,
                    background: '#fcfbfb',
                }}
            >
                <Stack>
                    <Typography variant='subtitle1' fontWeight={700} color='primary'>
                        {_('Simular otra fecha de siembra')}
                    </Typography>
                    <Typography variant='body2' color='primary'>
                        {_(
                            'Ajustá la fecha de siembra según el período crítico del cultivo, y anticipar la incidencia de plagas y el riesgo de heladas.'
                        )}
                    </Typography>
                </Stack>

                <Stack direction={compact ? 'column' : 'row'} spacing={2} alignItems={compact ? 'stretch' : 'center'}>
                    <MuiDatePicker
                        value={draftDate ? dayjs(draftDate) : null}
                        onChange={(date) => setDraftDate(date ? date.format('YYYY-MM-DD') : undefined)}
                        sx={{ width: compact ? '100%' : 240 }}
                    />
                    <Button
                        variant='text'
                        color='primary'
                        startIcon={simulatedDate ? <RefreshIcon /> : undefined}
                        disabled={!canSimulate}
                        onClick={() => setSimulatedDate(draftDate)}
                        data-testid='phenology-simulate'
                    >
                        {simulatedDate ? _('Volver a simular') : _('Simular')}
                    </Button>
                </Stack>

                {simulatedDate &&
                    (simulation.isLoading && !simulation.hybrids.length ? (
                        <Stack alignItems='center' py={4}>
                            <Spinner />
                        </Stack>
                    ) : (
                        <Stack spacing={2}>
                            <PhenologyTimeline hybrids={simulation.hybrids} compact={compact} />
                            <PhenologyLegend />
                            <Stack direction='row' spacing={2} justifyContent='flex-end'>
                                <Button variant='text' color='primary' onClick={() => setSimulatedDate(undefined)}>
                                    {_('Descartar')}
                                </Button>
                                <Button
                                    variant='contained'
                                    color='secondary'
                                    onClick={() => setConfirmOpen(true)}
                                    data-testid='phenology-apply-date'
                                >
                                    {_('Aplicar fecha')}
                                </Button>
                            </Stack>
                        </Stack>
                    ))}
            </Stack>

            <ModalV2
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title={_('Ajustar fecha de siembra')}
                maxWidth='xs'
                actions={
                    <Stack direction='row' spacing={2} justifyContent='flex-end' width='100%'>
                        <Button variant='text' color='primary' onClick={() => setConfirmOpen(false)}>
                            {_('Cancelar')}
                        </Button>
                        <Button variant='contained' color='primary' onClick={applyDate} data-testid='confirm-date'>
                            {_('Cambiar fecha')}
                        </Button>
                    </Stack>
                }
            >
                <Stack p={2}>
                    <Typography variant='body2' color='primary' textAlign='center'>
                        {_(
                            'Con la nueva fecha de siembra, cambia el ambiente del lote. Vas a tener que volver a configurarlo y elegir los híbridos para obtener una nueva recomendación.'
                        )}
                    </Typography>
                </Stack>
            </ModalV2>
        </Stack>
    );
}

export default PhenologyTab;
